import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard, Newspaper, Plus, Edit2, Trash2, LogOut,
  Search, Filter, AlertCircle, CheckCircle, X, Eye,
  TrendingUp, Users, FileText, Clock
} from 'lucide-react'

const TYPE_OPTIONS = ['招聘', '截止提醒', '经验', '趋势']
const STATUS_OPTIONS = [
  { value: 'published', label: '已发布', color: 'bg-green-100 text-green-700' },
  { value: 'draft', label: '草稿', color: 'bg-gray-100 text-gray-700' }
]

const AdminDashboardPage = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('dashboard')
  const [intelligence, setIntelligence] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [filterType, setFilterType] = useState('')

  const [form, setForm] = useState({
    type: '招聘',
    title: '',
    summary: '',
    source: '',
    publishTime: new Date().toISOString().split('T')[0],
    isUrgent: false,
    tags: '',
    status: 'published'
  })

  // 检查登录状态
  useEffect(() => {
    const token = localStorage.getItem('admin_token')
    if (!token) {
      navigate('/admin/login')
    }
  }, [navigate])

  // 获取统计数据
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token')
      const response = await fetch('/api/admin/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await response.json()
      if (data.status === 200) {
        setStats(data.data)
      }
    } catch (error) {
      console.error('获取统计失败:', error)
    }
  }

  // 获取情报列表
  const fetchIntelligence = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('admin_token')
      const params = new URLSearchParams()
      if (filterType) params.append('type', filterType)
      if (searchKeyword) params.append('keyword', searchKeyword)

      const response = await fetch(`/api/admin/intelligence?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await response.json()
      if (data.status === 200) {
        setIntelligence(data.data.list)
      }
    } catch (error) {
      console.error('获取情报失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
    fetchIntelligence()
  }, [filterType])

  // 搜索防抖
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchIntelligence()
    }, 300)
    return () => clearTimeout(timer)
  }, [searchKeyword])

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_user')
    navigate('/admin/login')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const token = localStorage.getItem('admin_token')

    const payload = {
      ...form,
      tags: form.tags.split(/[,，]/).map(t => t.trim()).filter(Boolean)
    }

    try {
      const url = editingItem
        ? `/api/admin/intelligence/${editingItem.id}`
        : '/api/admin/intelligence'
      const method = editingItem ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      })

      const data = await response.json()
      if (data.status === 200) {
        setShowModal(false)
        setEditingItem(null)
        setForm({
          type: '招聘',
          title: '',
          summary: '',
          source: '',
          publishTime: new Date().toISOString().split('T')[0],
          isUrgent: false,
          tags: '',
          status: 'published'
        })
        fetchIntelligence()
        fetchStats()
      } else {
        alert(data.message || '操作失败')
      }
    } catch (error) {
      alert('网络错误')
    }
  }

  const handleEdit = (item) => {
    setEditingItem(item)
    setForm({
      type: item.type,
      title: item.title,
      summary: item.summary,
      source: item.source,
      publishTime: item.publishTime,
      isUrgent: item.isUrgent,
      tags: item.tags.join(', '),
      status: item.status
    })
    setShowModal(true)
  }

  const handleDelete = async (id) => {
    if (!confirm('确定要删除这条情报吗？')) return

    try {
      const token = localStorage.getItem('admin_token')
      const response = await fetch(`/api/admin/intelligence/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      const data = await response.json()
      if (data.status === 200) {
        fetchIntelligence()
        fetchStats()
      }
    } catch (error) {
      alert('删除失败')
    }
  }

  const getTypeColor = (type) => {
    const colors = {
      '招聘': 'bg-blue-100 text-blue-700',
      '截止提醒': 'bg-red-100 text-red-700',
      '经验': 'bg-green-100 text-green-700',
      '趋势': 'bg-purple-100 text-purple-700'
    }
    return colors[type] || 'bg-gray-100 text-gray-700'
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* 侧边栏 */}
      <div className="w-64 bg-white shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">央</span>
            </div>
            <span className="font-bold text-gray-900">管理后台</span>
          </div>
        </div>

        <nav className="p-4 space-y-1">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'dashboard' ? 'bg-primary-50 text-primary-700' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <LayoutDashboard size={18} />
            <span>数据概览</span>
          </button>
          <button
            onClick={() => setActiveTab('intelligence')}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'intelligence' ? 'bg-primary-50 text-primary-700' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Newspaper size={18} />
            <span>情报管理</span>
          </button>
        </nav>

        <div className="absolute bottom-0 left-0 w-64 p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut size={18} />
            <span>退出登录</span>
          </button>
        </div>
      </div>

      {/* 主内容区 */}
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          {activeTab === 'dashboard' && (
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-6">数据概览</h1>

              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <FileText size={20} className="text-primary-600" />
                      <span className="text-2xl font-bold text-gray-900">{stats.total}</span>
                    </div>
                    <p className="text-sm text-gray-600">情报总数</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <CheckCircle size={20} className="text-green-600" />
                      <span className="text-2xl font-bold text-green-600">{stats.published}</span>
                    </div>
                    <p className="text-sm text-gray-600">已发布</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <AlertCircle size={20} className="text-red-600" />
                      <span className="text-2xl font-bold text-red-600">{stats.urgent}</span>
                    </div>
                    <p className="text-sm text-gray-600">紧急情报</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <Eye size={20} className="text-blue-600" />
                      <span className="text-2xl font-bold text-blue-600">{stats.totalViews}</span>
                    </div>
                    <p className="text-sm text-gray-600">总浏览量</p>
                  </div>
                </div>
              )}

              {stats && stats.byType && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="font-bold text-gray-900 mb-4">情报类型分布</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(stats.byType).map(([type, count]) => (
                      <div key={type} className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold text-gray-900">{count}</p>
                        <p className="text-sm text-gray-600">{type}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'intelligence' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900">情报管理</h1>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setEditingItem(null)
                    setForm({
                      type: '招聘',
                      title: '',
                      summary: '',
                      source: '',
                      publishTime: new Date().toISOString().split('T')[0],
                      isUrgent: false,
                      tags: '',
                      status: 'published'
                    })
                    setShowModal(true)
                  }}
                  className="bg-primary-600 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 hover:bg-primary-700"
                >
                  <Plus size={18} />
                  <span>新建情报</span>
                </motion.button>
              </div>

              {/* 筛选 */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
                <div className="flex flex-col md:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      placeholder="搜索标题或摘要..."
                      value={searchKeyword}
                      onChange={(e) => setSearchKeyword(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">全部类型</option>
                    {TYPE_OPTIONS.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 列表 */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {loading ? (
                  <div className="p-12 text-center">
                    <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-500">加载中...</p>
                  </div>
                ) : intelligence.length === 0 ? (
                  <div className="p-12 text-center">
                    <Newspaper size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">暂无情报数据</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">类型</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">标题</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">来源</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">发布时间</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {intelligence.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(item.type)}`}>
                              {item.type}
                              {item.isUrgent && <span className="ml-1 text-red-500">!</span>}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-gray-900 truncate max-w-xs">{item.title}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{item.source}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{item.publishTime}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              item.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {item.status === 'published' ? '已发布' : '草稿'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEdit(item)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button
                                onClick={() => handleDelete(item.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 编辑弹窗 */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto"
            >
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingItem ? '编辑情报' : '新建情报'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">类型</label>
                    <select
                      value={form.type}
                      onChange={(e) => setForm({ ...form, type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {TYPE_OPTIONS.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">发布时间</label>
                    <input
                      type="date"
                      value={form.publishTime}
                      onChange={(e) => setForm({ ...form, publishTime: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">标题</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="请输入情报标题"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">摘要</label>
                  <textarea
                    value={form.summary}
                    onChange={(e) => setForm({ ...form, summary: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="请输入情报摘要"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">来源</label>
                    <input
                      type="text"
                      value={form.source}
                      onChange={(e) => setForm({ ...form, source: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="如：国聘网"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">标签（用逗号分隔）</label>
                    <input
                      type="text"
                      value={form.tags}
                      onChange={(e) => setForm({ ...form, tags: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="如：央企, 校招, 高薪"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={form.isUrgent}
                      onChange={(e) => setForm({ ...form, isUrgent: e.target.checked })}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <span className="text-sm text-gray-700">标记为紧急</span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={form.status === 'published'}
                      onChange={(e) => setForm({ ...form, status: e.target.checked ? 'published' : 'draft' })}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <span className="text-sm text-gray-700">立即发布</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    {editingItem ? '保存修改' : '创建情报'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default AdminDashboardPage
